import { type Editor, type TLShape, createShapeId } from "tldraw";
import api from "@/utils/axios-utils";
import { SLIDE_EMBED_TYPE } from "./shapes/SlideEmbedShapeUtil";
import { parseSlideHtml } from "./slide-content-parser";

/**
 * Builds a beforeCreateHandler that stamps user-authored shapes with teacher
 * metadata (role, createdBy, optional layer). source !== "user" passes through
 * untouched.
 *
 * Input:  teacherUserId (DB user id), optional layer key (watched student id
 *         when teacher draws on a student's personal canvas).
 * Output: a (shape, source) handler suitable for editor.sideEffects.
 */
export function buildTeacherShapeStampHandler(
  teacherUserId: number,
  layer?: string,
): (shape: TLShape, source: "user" | "remote") => TLShape {
  return (shape, source) => {
    if (source !== "user") return shape;
    return {
      ...shape,
      meta: {
        ...(shape.meta as object),
        role: "teacher",
        createdBy: teacherUserId,
        ...(layer !== undefined ? { layer } : {}),
      },
    };
  };
}

const ASSESSMENT_SLIDE_TYPES = new Set([
  "exit_ticket",
  "pre_assessment",
  "custom",
]);

// Converts the editor's local snapshot format into the room-sync format
// expected by the backend /import-snapshot endpoint.
export function editorSnapshotToRoomSnapshot(
  editor: Editor,
): Record<string, unknown> {
  const { document } = editor.getSnapshot();
  return {
    documents: Object.values(document.store).map((record) => ({
      state: record,
      lastChangedClock: 0,
    })),
    schema: document.schema,
    clock: 1,
    tombstones: {},
  };
}

// Fetches lesson slides for the given lesson IDs, creates SlideEmbed shapes on
// the editor, persists the snapshot to the backend, and parses AI metadata in
// the background. Assessment slides are excluded — they are student-facing
// interactive activities that don't belong on the shared canvas.
export async function importLessonSlides(
  editor: Editor,
  lessonIds: number[],
  spaceId: string,
  userId: number,
): Promise<void> {
  const allResponses = await Promise.all(
    lessonIds.map((id) => api.get(`/lessons/${id}/slides`)),
  );
  const slides = allResponses
    .flatMap(
      (res) =>
        (res.data.slides ?? []) as Array<{
          slide_html: string;
          metadata?: { slide_type?: string };
        }>,
    )
    .filter((s) => !ASSESSMENT_SLIDE_TYPES.has(s.metadata?.slide_type ?? ""));
  if (slides.length === 0) return;

  const SLIDE_W = 960;
  const SLIDE_H = 700;
  const GAP = 40;

  const alreadyImported = editor
    .getCurrentPageShapes()
    .some((s) => (s as any).type === SLIDE_EMBED_TYPE);

  if (!alreadyImported) {
    const shapeIds = slides.map(() => createShapeId());

    (editor.createShapes as any)(
      slides.map((slide, i) => ({
        id: shapeIds[i],
        type: SLIDE_EMBED_TYPE,
        x: 0,
        y: i * (SLIDE_H + GAP),
        meta: { role: "teacher", createdBy: userId, slideContent: [] },
        props: { url: slide.slide_html, w: SLIDE_W, h: SLIDE_H },
      })),
    );

    await api.patch(`/spaces/${spaceId}/import-snapshot`, {
      snapshot: editorSnapshotToRoomSnapshot(editor),
      markImported: true,
    });

    // Parse AI metadata in background — first slide prioritized (visible in viewport)
    parseSlideHtml(slides[0].slide_html).then((content) => {
      (editor.updateShapes as any)([
        {
          id: shapeIds[0],
          type: SLIDE_EMBED_TYPE,
          meta: { role: "teacher", createdBy: userId, slideContent: content },
        },
      ]);
    });
    for (let i = 1; i < slides.length; i++) {
      const idx = i;
      parseSlideHtml(slides[idx].slide_html).then((content) => {
        (editor.updateShapes as any)([
          {
            id: shapeIds[idx],
            type: SLIDE_EMBED_TYPE,
            meta: { role: "teacher", createdBy: userId, slideContent: content },
          },
        ]);
      });
    }
  } else {
    await api.patch(`/spaces/${spaceId}/import-snapshot`, {
      snapshot: editorSnapshotToRoomSnapshot(editor),
      markImported: true,
    });
  }
}
